import { IconButton, TableCell, TableRow } from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../app/hooks";
import IconWrapper from "../../../common/components/functional/icons/IconWrapper";
import { StripedTable } from "../../../common/components/functional/stripedTable/StripedTable";
import { StripedTableHeader } from "../../../common/components/functional/stripedTable/types";
import { localizeNames } from "../../../common/helpers/common";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";
import { ReactComponent as DeleteIcon } from "../../../common/icons/deleteDark.svg";
import { ReactComponent as EditIcon } from "../../../common/icons/editDark.svg";
import theme from "../../../theme";
import { TalentBlacklist } from "../../talents/talentAPI";
import { BLOCKING_PARTIES } from "./constants";

type Props = {
  blacklists: TalentBlacklist[];
  onEditClick: (entry: TalentBlacklist) => void;
  deleteBlacklistRow: (blacklistEntryId: string, version: number) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
};

export const BlacklistTable = ({
  blacklists,
  onEditClick,
  deleteBlacklistRow,
  isCollapsed,
  onToggleCollapse,
}: Props) => {
  const { t } = useTranslation("");
  const { localized } = useAppSelector((state) => state);

  const HEADERS: StripedTableHeader[] = [
    { key: "customerCompany", label: t("customerCompany"), width: "15%" },
    { key: "organization", label: t("talentProfile:organization"), width: "15%" },
    { key: "workLocation", label: t("workLocation"), width: "15%" },
    { key: "workTitles", label: t("workTitles"), width: "15%" },
    { key: "createdAt", label: t("talentProfile:createdAt"), width: "7%" },
    { key: "blacklister", label: t("talentProfile:blacklister"), width: "7%" },
    { key: "reason", label: t("talentProfile:reason"), width: "18%" },
    { key: "actions", label: "", width: "8%" },
  ];

  const emptyRow = [
    <TableRow key="empty">
      <TableCell colSpan={8}>{t("noItems")}</TableCell>
    </TableRow>,
  ];

  const blacklistRows = blacklists.map((blacklist) => {
    const workTitleNames = localizeNames(
      localized.workTitle,
      blacklist.workTitles,
    );
    return (
      <TableRow key={blacklist.id}>
        <TableCell width="15%">
          <Link
            to={`/home/customers/${blacklist.customerCompanyId}/basicInfo?org=${blacklist.customerOrganizationId}`}
            style={{
              textDecoration: "none",
              color: theme.palette.primary.main,
            }}
          >
            {blacklist.customerCompanyName}
          </Link>
        </TableCell>
        <TableCell width="15%">
          {blacklist.customerOrganizationName || t("talentProfile:allBlocked")}
        </TableCell>
        <TableCell width="15%">
          {blacklist.workLocationName || t("talentProfile:allBlocked")}
        </TableCell>
        <TableCell width="15%">
          {workTitleNames || t("talentProfile:allBlocked")}
        </TableCell>
        <TableCell width="5%">
          {blacklist.createdAt &&
            format(new Date(blacklist.createdAt), leadingZerosDateFormat)}
        </TableCell>
        <TableCell width="5%">
          {t(
            `talentProfile:${
              BLOCKING_PARTIES.find(
                (party) => party.name === blacklist.blockingParty,
              )?.label
            }`,
          )}
        </TableCell>
        <TableCell width="26%">{blacklist.reason}</TableCell>
        <TableCell
          width="2%"
          sx={{
            whiteSpace: "nowrap",
            minWidth: "90px",
            padding: 0,
            paddingRight: 1,
            textAlign: "right",
          }}
        >
          <IconButton
            onClick={() => onEditClick(blacklist)}
            sx={{ width: 44, height: 44 }}
          >
            <IconWrapper
              icon={EditIcon}
              sx={{
                fill: "none",
                stroke: "#000000de",
                width: 18,
                height: 18,
                viewBox: "0 0 28 28",
              }}
            />
          </IconButton>
          <IconButton
            onClick={() => deleteBlacklistRow(blacklist.id, blacklist.version)}
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  });

  const ROWS = blacklists.length ? blacklistRows : emptyRow;

  return (
    <StripedTable
      maxWidth="1800px"
      columnHeaders={HEADERS}
      itemRows={ROWS}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
    />
  );
};
